/**
 * Sentry + monitoring gate for the driver app.
 *
 * Must be imported first in main.tsx so the SDK is initialised before React
 * renders and before any module-level code can throw uncaught.
 *
 * Gating is deliberately fail-open on configuration: each tool is switched on by
 * its OWN credential, and VITE_APP_ENV is only a label. An unset or misspelled
 * VITE_APP_ENV therefore costs an accurate environment tag — it cannot silently
 * switch monitoring off, which is what an allowlist of approved environments
 * would do.
 *
 * What still keeps things quiet:
 *   - `import.meta.env.PROD` is false under `vite dev`, so local never reports.
 *   - No DSN / no PostHog key means that tool stays off. Scope those variables
 *     per-environment in Vercel to choose which deploys report.
 *   - `VITE_APP_ENV=preview` is an explicit opt-out, for preview deploys that
 *     inherit production credentials but should stay silent.
 */
import * as Sentry from '@sentry/react';

const appEnv = import.meta.env.VITE_APP_ENV as string | undefined;
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as
  | string
  | undefined;

/**
 * Tag applied to every Sentry event and every PostHog event. Never undefined:
 * falls back to the build type so events stay attributable even when
 * VITE_APP_ENV was never set.
 */
export const environment =
  appEnv ?? (import.meta.env.PROD ? 'production' : 'development');

/** True on any deployed build that has not explicitly opted out. */
export const monitoringEnabled =
  import.meta.env.PROD && environment !== 'preview';

export const sentryEnabled = monitoringEnabled && Boolean(dsn);
export const posthogEnabled = monitoringEnabled && Boolean(posthogKey);

/**
 * Distributed tracing, off until the backend is known to accept it.
 *
 * Browser tracing attaches `sentry-trace` and `baggage` to every request
 * matching tracePropagationTargets. Those are non-simple headers, so the
 * browser preflights, and an API that does not list them in
 * Access-Control-Allow-Headers fails the preflight and blocks the call. Error
 * reporting is unaffected either way — only performance data is lost.
 *
 * To re-enable: confirm a preflight against the deployed API echoes both
 * headers, then flip this to true.
 *
 *   curl -sI -X OPTIONS "$API/api/v1/health" \
 *     -H 'Origin: https://<frontend>' \
 *     -H 'Access-Control-Request-Method: GET' \
 *     -H 'Access-Control-Request-Headers: authorization,sentry-trace,baggage' \
 *     | grep -i access-control-allow-headers
 *
 * `tracesSampleRate: 0` is NOT a substitute: browserTracingIntegration still
 * propagates a sentry-trace header on unsampled traces, so the preflight would
 * fail identically. The integration itself has to be absent.
 */
const TRACING_ENABLED = false;

/**
 * Resolved config, readable from the browser console on a deployed build.
 * Production builds strip `console.*` (vite.config `drop: ['console']`), so a
 * startup warning would vanish exactly where it is needed — this survives.
 */
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__RELAY_MONITORING__ = {
    environment,
    appEnvSet: Boolean(appEnv),
    sentryEnabled,
    posthogEnabled,
    tracingEnabled: sentryEnabled && TRACING_ENABLED,
  };
}

const REDACTED = '[redacted]';

/**
 * Strip secrets out of URLs before they reach Sentry.
 *
 * Necessary, not paranoia: the Auth0 callback lands on `?code=...&state=...`
 * and driver invitations are opened as `?token=...`. Sentry records
 * `window.location.href` in breadcrumbs and transaction names, so without this
 * live authorization codes and invitation tokens would be shipped upstream.
 *
 * Over-redaction is intentional: `postcode=` or `keyword=` also match. Losing a
 * little debugging detail is the right trade against leaking a credential.
 */
const SENSITIVE_PARAM_RE =
  /([?&#])([\w.-]*(?:token|code|state|secret|password|key)[\w.-]*)=([^&#\s]*)/gi;

export function redactUrl(url: string): string {
  if (!url) return url;
  return url.replace(
    SENSITIVE_PARAM_RE,
    (_match, delimiter: string, key: string) =>
      `${delimiter}${key}=${REDACTED}`,
  );
}

if (sentryEnabled) {
  Sentry.init({
    dsn,
    environment,

    integrations: TRACING_ENABLED ? [Sentry.browserTracingIntegration()] : [],

    // Staging is low-traffic so trace everything; production samples.
    tracesSampleRate: TRACING_ENABLED
      ? environment === 'production'
        ? 0.1
        : 1.0
      : 0,

    tracePropagationTargets: TRACING_ENABLED && apiBaseUrl ? [apiBaseUrl] : [],

    sendDefaultPii: false,

    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = redactUrl(event.request.url);
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((crumb) =>
          typeof crumb.data?.url === 'string'
            ? { ...crumb, data: { ...crumb.data, url: redactUrl(crumb.data.url) } }
            : crumb,
        );
      }
      return event;
    },

    beforeBreadcrumb(crumb) {
      if (typeof crumb.data?.url === 'string') {
        crumb.data.url = redactUrl(crumb.data.url);
      }
      return crumb;
    },
  });
}
